import { useEffect, useState, useCallback } from "react";
import { SERVER_URL } from "../../../constants";

const EMPTY_DATA = { unassigned: [], groups: [] };

// A group member with no name is a "ghost": someone whose stay was moved to
// another campus but who's still attached to this campus's group. The backend
// returns them with name/dates null for this campus, and the Activity Planner
// already filters them out — so drop them here too, keeping the timetable and
// its member counts clean. (Backend cleanup on campus-move is the real fix.)
const hasName = (p) => String(p?.name || "").trim() !== "";
const dropGhosts = (arr) => (Array.isArray(arr) ? arr.filter(hasName) : []);

export default function useActivitiesTimetable(
  date,
  campus,
  slot,
  options = {}
) {
  const { enabled = true } = options;

  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!enabled || !date || !campus || !slot) {
      setLoading(false);
      setError(null);
      setData(EMPTY_DATA);
      return EMPTY_DATA;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${SERVER_URL}api/activities/timetable?date=${date}&campus=${campus}&slot=${slot}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer")}`,
          },
        }
      );

      if (!res.ok) throw new Error("Failed to load activities timetable");

      const json = await res.json();

      const nextData = {
        unassigned: dropGhosts(json?.unassigned),
        groups: (json?.groups || []).map((g) => ({
          ...g,
          members: dropGhosts(g?.members),
        })),
      };

      setData(nextData);
      return nextData;
    } catch (e) {
      setError(e.message);
      setData(EMPTY_DATA);
      return EMPTY_DATA;
    } finally {
      setLoading(false);
    }
  }, [date, campus, slot, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}